/**
 * 외부 3D 모델(GLB)을 조각으로 쓰는 길.
 *
 * 지금까지 이 프로젝트의 기물은 전부 코드가 그렸다. 그 규칙을 여기서 처음 깬다 —
 * Tripo 로 뽑은 모델 스물두 점이 들어왔기 때문이다.
 *
 * **절차 조각을 지우지 않는다.** 조각 정의에 `glb` 한 줄이 붙으면 그 조각은
 * GLB 로 그려지고, 없거나 로드가 실패하면 원래 `build()` 가 그린다. 그래서
 *
 *  - 모델이 아직 안 온 조각과 온 조각이 한 맵에 섞여 있어도 된다
 *  - 네트워크가 끊겨도 맵에 구멍이 나지 않는다 (절차 버전이 그 자리를 지킨다)
 *  - GLB 가 마음에 안 들면 그 한 줄만 지우면 원래대로 돌아온다
 *
 * ## 크기는 누가 정하는가
 *
 * Tripo 출력은 **전부 1×1×1 로 정규화**되어 있다(가장 긴 축이 1). 실제 크기 정보가
 * 없다는 뜻이다. 그렇다고 조각마다 실측값을 손으로 적으면 `dims` 계약이 죽는다 —
 * 에디터에서 폭을 늘려도 모델은 안 따라오게 된다.
 *
 * 그래서 **절차 버전의 bbox 에 맞춘다.** 같은 조각을 코드가 그렸을 때 차지하던
 * 부피를 그대로 GLB 에게 준다. 이러면
 *
 *  - 좌표를 하나도 안 고치고 갈아 끼울 수 있다 (이미 배치된 스물한 점이 그랬다)
 *  - `dims` 를 바꾸면 GLB 도 같이 커진다 — 계약이 살아 있다
 *  - 겹침 검사·충돌이 예전 값 그대로 맞는다
 *
 * 축마다 다른 배율을 주면 물건이 찌그러지므로 **세 축 비율 중 가장 작은 것**을
 * 균일하게 쓴다. 절차 bbox 안에 반드시 들어간다는 뜻이다.
 */

import * as THREE from 'three';
import { PIECE_BY_ID } from './index';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';

const BASE = '/models/sahol/';

let loader: GLTFLoader | null = null;
function getLoader(): GLTFLoader {
  if (!loader) {
    const draco = new DRACOLoader();
    draco.setDecoderPath('/draco/');
    loader = new GLTFLoader();
    loader.setDRACOLoader(draco);
  }
  return loader;
}

/**
 * 원본 캐시 — 파일당 한 번만 받는다.
 *
 * 영혼 항아리는 맵에 네 벌 놓인다. 네 번 받으면 같은 것을 네 번 디코딩하는 셈이라
 * 받아 둔 것을 `clone()` 해서 쓴다. 재질과 텍스처는 clone 이 공유하므로
 * (three 의 clone 은 material 을 참조로 넘긴다) GPU 메모리도 한 벌이다.
 */
const cache = new Map<string, Promise<THREE.Object3D>>();

function loadRaw(name: string): Promise<THREE.Object3D> {
  let p = cache.get(name);
  if (!p) {
    p = new Promise((resolve, reject) => {
      getLoader().load(
        name.startsWith('/') ? `${name}.glb` : `${BASE}${name}.glb`,   // 절대 경로면 그대로
        (gltf) => resolve(gltf.scene),
        undefined,
        (err) => reject(err),
      );
    });
    cache.set(name, p);
  }
  return p;
}

/**
 * 경로를 직접 주고 받아 온다 — 소품 폴더(`BASE`) 밖의 모델용.
 *
 * 시신처럼 카탈로그 조각이 아닌 물건이 있다. 그것도 draco 로 압축돼 있으므로
 * 디코더가 물린 이 로더를 써야 한다. 캐시는 같이 쓴다.
 */
export function loadGlbUrl(url: string): Promise<THREE.Object3D> {
  let p = cache.get(url);
  if (!p) {
    p = new Promise((resolve, reject) => {
      getLoader().load(url, (g) => resolve(g.scene), undefined, (e) => reject(e));
    });
    cache.set(url, p);
  }
  return p.then((o) => o.clone(true));
}

/** 절차 버전이 차지하던 부피. 여기에 GLB 를 맞춘다 */
export interface FitBox {
  min: THREE.Vector3;
  max: THREE.Vector3;
}

/**
 * GLB 를 받아 `target` 그룹 안에 넣는다.
 *
 * **비동기인데 아무것도 기다리게 하지 않는다.** 부르는 쪽은 빈 그룹(정확히는
 * 절차 버전이 들어 있는 그룹)을 즉시 돌려받고, 모델이 도착하면 그때 교체된다.
 * 씬 구축을 GLB 22개의 네트워크 왕복에 묶어 두면 에디터를 열 때마다 몇 초씩
 * 검은 화면을 보게 된다.
 *
 * @param fallback **로드가 실패했을 때만** 붙일 절차 메시.
 *   부르는 쪽은 이것을 씬에 넣지 않은 채로 넘긴다 — 넣어 두고 나중에 감추는
 *   방식은 배칭(`bakeFloor`)이 `visible` 을 무시해서 두 벌이 겹쳐 버린다.
 */
export function attachGlb(target: THREE.Group, name: string, fit: FitBox, fallback: THREE.Object3D[]): void {
  loadRaw(name)
    .then((raw) => {
      const model = raw.clone(true);

      // 원본 크기 — Tripo 정규화가 언제나 1 인 것은 아니라 실제로 잰다
      const box = new THREE.Box3().setFromObject(model);
      const size = box.getSize(new THREE.Vector3());
      if (size.x < 1e-6 || size.y < 1e-6 || size.z < 1e-6) {
        // 빈 모델이었다. 그럴 바엔 절차본을 그린다
        for (const f of fallback) target.add(f);
        return;
      }

      const want = fit.max.clone().sub(fit.min);

      // **높이를 맞춘다.** 세 축 비율 중 최솟값(contain)을 처음에 썼는데, 절차
      // 버전과 Tripo 의 종횡비가 다르면 모델이 제 부피의 절반만 채우고 쪼그라들었다
      // (신단이 그랬다 — 코드는 넓적하게, Tripo 는 높다랗게 만들었다).
      //
      // 바닥에 선 물건에서 사람이 크기로 읽는 것은 높이다. 폭이 설계와 조금 달라도
      // 어색하지 않지만, 키가 반이면 즉시 틀려 보인다.
      let s = want.y / size.y;

      // 다만 그 배율로 옆이 지나치게 퍼지면 옆 물건을 뚫는다. 1.3 배까지만 봐주고
      // 넘으면 거기서 끊는다 — 조금 낮은 편이 벽을 뚫는 것보다 낫다.
      const SPILL = 1.3;
      const cap = Math.min((want.x * SPILL) / size.x, (want.z * SPILL) / size.z);
      s = Math.min(s, cap);
      model.scale.setScalar(s);

      // 스케일 후 다시 재서 앉힌다. **바닥은 맞추고 XZ 는 중심을 맞춘다** —
      // 물건은 바닥에 서 있지 가운데 떠 있지 않다
      const after = new THREE.Box3().setFromObject(model);
      const c = after.getCenter(new THREE.Vector3());
      const fitC = fit.min.clone().add(fit.max).multiplyScalar(0.5);
      model.position.set(fitC.x - c.x, fit.min.y - after.min.y, fitC.z - c.z);

      for (const o of model.children) void o;
      model.traverse((o) => {
        if ((o as THREE.Mesh).isMesh) {
          o.castShadow = true;
          o.receiveShadow = true;
        }
      });

      model.name = `glb:${name}`;
      target.add(model);
      /* **여닫이 부품** (2026-09-15, Chris: 「변기 열기 … 이불 … 다 상호작용」). 조각의 `openable[].node` 가 가리키는
       * 노드(블렌더 오브젝트 이름)를 `open:<part>` 그룹으로 감싼다. node 가 '*' 면 모델 통째(이불·쿠션 들추기).
       * 그 뒤 props.ts 에 알려 기다리던 덮개 프롭이 붙는다. 스케일·위치는 모델 그룹에 있으므로 감싼 그룹은 모델 안이다 */
      const pieceId = target.userData.pieceId as string | undefined;
      const specs = pieceId ? PIECE_BY_ID[pieceId]?.openable : undefined;
      let wrapped = 0;
      for (const sp of specs ?? []) {
        if (!sp.node) continue;
        if (sp.node === '*') {
          const grp = new THREE.Group(); grp.name = `open:${sp.part}`;
          // 모델 그룹 자체를 감싼다 — 축은 조각 로컬(pivot 은 조각 좌표)
          target.remove(model); grp.add(model); target.add(grp); wrapped++;
          continue;
        }
        const node = model.getObjectByName(sp.node);
        if (!node) { console.warn(`[glb] ${name}: openable node '${sp.node}' 없음`); continue; }
        // 모델 안의 노드를 조각 로컬로 꺼내 감싼다: 노드의 조각 기준 행렬을 그룹에 넘긴다
        target.updateMatrixWorld(true);
        const m = new THREE.Matrix4().copy(target.matrixWorld).invert().multiply(node.matrixWorld);
        node.removeFromParent();
        const grp = new THREE.Group(); grp.name = `open:${sp.part}`;
        node.matrix.copy(m); node.matrix.decompose(node.position, node.quaternion, node.scale);
        grp.add(node); target.add(grp); wrapped++;
      }
      if (wrapped) (globalThis as unknown as { __glbPartsReady?: (k: string) => void }).__glbPartsReady?.(target.userData.pieceKey as string);
    })
    .catch((err) => {
      console.warn(`[glb] ${name} 로드 실패 — 절차 버전을 쓴다`, err);
      // 배칭은 이미 끝났으니 개별 draw call 로 그려진다. 맵에 구멍이 나는 것보다 낫다
      for (const f of fallback) target.add(f);
    });
}

/** 미리 받아 둔다. 에디터에서 첫 프레임의 팝인을 줄인다 */
export function preloadGlb(names: string[]): void {
  for (const n of names) loadRaw(n).catch(() => {});
}
